import { Grid2, Link } from '@mui/material';
import type { FormikHelpers } from 'formik';
import { ErrorMessage, Form, Formik } from 'formik';
import { Link as RouterLink } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';
import type { Schema } from 'yup';

import { FormikTextField } from '@components/FormikFields';
import { PasswordRules } from '@components/PasswordRules';
import RoundedButton from '@components/RoundedButton/index';

import * as ROUTES from '@constants/routes';

import { useUpdatePassword } from '@hooks/api/session';
import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

const messages = defineMessages({
  changePassword: {
    id: 'changePassword.changePassword',
    defaultMessage: 'Change Password',
  },
  currentPassword: {
    id: 'changePassword.currentPassword',
    defaultMessage: 'Current password',
  },
  newPassword: {
    id: 'changePassword.newPassword',
    defaultMessage: 'New Password',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
  forgotPassword: {
    id: 'changePassword.forgotPassword',
    defaultMessage: 'Forgot your password?',
  },
  passwordIncorrect: {
    id: 'changePassword.passwordIncorrect',
    defaultMessage: 'Current password incorrect',
  },
  passwordDifferent: {
    id: 'changePassword.passwordDifferent',
    defaultMessage: 'Password must not be the same as old password',
  },
  error: {
    id: 'errorMessage.default',
    defaultMessage: 'An error occurred. Please try again later.',
  },
});

interface FormValues {
  currentPassword: string;
  newPassword: string;
  submit: string | null;
}

function UpdatePasswordPage() {
  const { formatMessage } = useIntl();
  const [changePassword] = useUpdatePassword();
  const { passwordSchema } = useCommonSchemas();

  const validationSchema: Schema<Partial<FormValues>> = Yup.object().shape({
    currentPassword: Yup.string(),
    newPassword: passwordSchema.notOneOf(
      [Yup.ref('currentPassword')],
      formatMessage(messages.passwordDifferent)
    ),
  });

  const initialValues: FormValues = {
    currentPassword: '',
    newPassword: '',
    submit: null,
  };

  // Validate and request password reset
  async function handleSubmit(
    values: FormValues,
    { resetForm, setErrors }: FormikHelpers<FormValues>
  ) {
    const { errors } = await changePassword({
      input: {
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      },
    });
    if (errors?.length) {
      setErrors({
        submit:
          errors.map(({ message }) => message).join(', ') ||
          formatMessage(messages.error),
      });
    } else {
      resetForm();
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting }) => (
        <Form>
          <FormikTextField
            autoComplete="current-password"
            name="currentPassword"
            label={formatMessage(messages.currentPassword)}
            placeholder="••••••••••"
            required
            type="password"
          />
          <FormikTextField
            autoComplete="new-password"
            name="newPassword"
            label={formatMessage(messages.newPassword)}
            placeholder="••••••••••"
            required
            type="password"
          />

          <PasswordRules />

          <Grid2
            container
            justifyContent="center"
            sx={{ py: 2 }}
            size={{ xs: 12 }}
          >
            <Link
              component={RouterLink}
              to={ROUTES.FORGOT_PASSWORD}
              color="text.secondary"
            >
              {formatMessage(messages.forgotPassword)}
            </Link>
          </Grid2>

          <ErrorMessage
            name="submit"
            render={(e) => {
              if (typeof e !== 'string') {
                return null;
              }
              return <p className="error-msg">{e}</p>;
            }}
          />

          <RoundedButton disabled={isSubmitting} type="submit">
            {formatMessage(messages.submit)}
          </RoundedButton>
        </Form>
      )}
    </Formik>
  );
}

export default UpdatePasswordPage;
